// src/views/Settings/org/OrgOverviewSection.jsx
//
// Los datos de la organización. Salió del modal `OrganizationDetailView`, que
// era una pantalla completa sin URL: no se podía enlazar, ni recargar sin
// perder el sitio, ni tener dos abiertas.
import React from "react";
import GlassPanel from "../../../components/ui/GlassPanel";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Users, Building2, Crown, Calendar, Copy, Globe, Mail, Home } from "lucide-react";
import { PRIMARY, BUTTON_SIZE } from '../../../lib/buttonStyles';

export default function OrgOverviewSection({ organization, role, isCurrent, onSwitchContext }) {
  if (!organization) return null;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-white">{organization.name}</h1>
          <p className="truncate text-gray-400">{organization.description || 'No description'}</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge
            className={
              organization.status === 'ACTIVE'
                ? 'bg-green-500/20 text-green-400 border-green-500/30'
                : 'bg-red-500/20 text-red-400 border-red-500/30'
            }
          >
            {organization.status === 'ACTIVE' ? 'ACTIVE' : organization.status || 'INACTIVE'}
          </Badge>
          {/* Cambiar de contexto es navegación, no configuración — pero se ofrece
              aquí porque es exactamente donde surge la intención. Si ya estás en
              esta organización, el botón no aparece: una opción que no hace nada
              es peor que no tenerla. */}
          {!isCurrent && (
            <Button
              onClick={() => onSwitchContext?.(organization)}
              className={PRIMARY}
            >
              <Home className="mr-2 h-4 w-4" />
              Switch to this organization
            </Button>
          )}
        </div>
      </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <GlassPanel className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Members</p>
                  <p className="text-2xl font-bold text-white">{organization.total_members || 0}</p>
                </div>
              </div>
            </GlassPanel>
            <GlassPanel className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Projects</p>
                  <p className="text-2xl font-bold text-white">{organization.total_projects || 0}</p>
                </div>
              </div>
            </GlassPanel>
            <GlassPanel className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center">
                  <Crown className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Your Role</p>
                  <p className="text-xl font-bold text-white capitalize">{role}</p>
                </div>
              </div>
            </GlassPanel>
            <GlassPanel className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-600/20 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Created</p>
                  <p className="text-sm font-bold text-white">
                    {new Date(organization.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </GlassPanel>
          </div>

            <GlassPanel className="p-8">
              <h2 className="text-xl font-bold text-white mb-6">Organization Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-400">Organization ID</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-white font-mono text-sm">{organization.id}</p>
                    <button
                      onClick={() => navigator.clipboard.writeText(organization.id)}
                      className="p-1 hover:bg-white/10 rounded"
                    >
                      <Copy className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>
                {organization.domain && (
                  <div>
                    <label className="text-sm text-gray-400">Domain</label>
                    <p className="text-white mt-1">{organization.domain}</p>
                  </div>
                )}
                {organization.website && (
                  <div>
                    <label className="text-sm text-gray-400">Website</label>
                    <a
                      href={organization.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-400 hover:text-purple-300 flex items-center gap-1 mt-1"
                    >
                      {organization.website}
                      <Globe className="w-4 h-4" />
                    </a>
                  </div>
                )}
                {organization.contact_email && (
                  <div>
                    <label className="text-sm text-gray-400">Contact Email</label>
                    <a
                      href={`mailto:${organization.contact_email}`}
                      className="text-purple-400 hover:text-purple-300 flex items-center gap-1 mt-1"
                    >
                      {organization.contact_email}
                      <Mail className="w-4 h-4" />
                    </a>
                  </div>
                )}
                {organization.industry && (
                  <div>
                    <label className="text-sm text-gray-400">Industry</label>
                    <p className="text-white mt-1">{organization.industry}</p>
                  </div>
                )}
                {organization.size && (
                  <div>
                    <label className="text-sm text-gray-400">Company Size</label>
                    <p className="text-white mt-1">{organization.size}</p>
                  </div>
                )}
                {organization.country && (
                  <div>
                    <label className="text-sm text-gray-400">Country</label>
                    <p className="text-white mt-1">{organization.country}</p>
                  </div>
                )}
              </div>
            </GlassPanel>
    </div>
  );
}
